import { apiGet, apiPost, apiDelete, planningPath } from './PlanningApiClient';

/** Quote vs invoice difference for a vendor. Amounts are exact decimal strings in the budget's currency. */
export interface BudgetVariance {
  id: string;
  vendor_id: string | null;
  vendor_name: string;
  original_quote_amount: number;
  actual_invoice_amount: number;
  variance_amount: number;
  variance_percentage: number;
  status: 'flagged' | 'acknowledged' | 'approved' | 'rejected';
  reason: string | null;
  approval_notes: string | null;
  approved_by: string | null;
  requires_approval: boolean;
  variance_direction: 'favorable' | 'unfavorable' | null;
  /** The budget's currency; null while the project has none. */
  currency: string | null;
}

interface ApiVariance extends Omit<BudgetVariance, 'original_quote_amount' | 'actual_invoice_amount' | 'variance_amount' | 'variance_percentage'> {
  original_quote_amount: string | number;
  actual_invoice_amount: string | number;
  variance_amount: string | number;
  variance_percentage: string | number;
}

const fromApi = (v: ApiVariance): BudgetVariance => ({
  ...v,
  original_quote_amount: Number(v.original_quote_amount) || 0,
  actual_invoice_amount: Number(v.actual_invoice_amount) || 0,
  variance_amount: Number(v.variance_amount) || 0,
  variance_percentage: Number(v.variance_percentage) || 0,
});

export async function loadVariances(): Promise<BudgetVariance[]> {
  const res = await apiGet<{ variances: ApiVariance[] }>(planningPath('/budget-variances'));
  return (res.variances ?? []).map(fromApi);
}

export async function createVariance(input: { vendor_name: string; original_quote_amount: string; actual_invoice_amount: string; reason?: string }): Promise<BudgetVariance> {
  return fromApi(await apiPost<ApiVariance>(planningPath('/budget-variances'), { ...input, reason: input.reason || null }));
}

/** The server records who decided: the signed-in person, not a name sent from here. */
export async function approveVariance(id: string, notes: string): Promise<BudgetVariance> {
  return fromApi(await apiPost<ApiVariance>(planningPath(`/budget-variances/${id}/approve`), { notes }));
}

export async function rejectVariance(id: string, notes: string): Promise<BudgetVariance> {
  return fromApi(await apiPost<ApiVariance>(planningPath(`/budget-variances/${id}/reject`), { notes }));
}

export async function deleteVariance(id: string): Promise<void> {
  await apiDelete(planningPath(`/budget-variances/${id}`));
}
